import React, { useEffect } from 'react';

import { expVal } from '@confluence/feature-experiments';

import { ANALYTICS_OPERATIONAL_SUBJECT } from '../constants/snippetAnalytics.ts';
import { useSnippetAnalytics } from '../hooks/analytics/useSnippetAnalytics.ts';
import { useSnippetsFetchAnalytics } from '../hooks/analytics/useSnippetsFetchAnalytics.ts';

import { useIsSnippetCreateEnabled } from '../hooks/embedded-confluence/useIsSnippetCreateEnabled.ts';
import { useIsEmbeddedConfluence_DO_NOT_USE } from '@confluence/embedded-confluence-check';

import { useSnippetsActions } from './snippetsStore.ts';

/**
 * Provider component that ensures snippets are fetched before the component tree renders.
 * The first fetch resolves site config (isSnippetSharingForSiteEnabled) inside the fetch flow and stores it.
 */
export const SnippetsProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
	const snippetsActions = useSnippetsActions();
	const { fireFailedAnalytic } = useSnippetAnalytics();
	useSnippetsFetchAnalytics();

	const isEmbeddedConfluence = useIsEmbeddedConfluence_DO_NOT_USE();
	const isSnippetCreateEnabled = useIsSnippetCreateEnabled();

	useEffect(() => {
		if (!expVal('cc_snippets', 'isEnabled', false)) {
			return;
		}
		if (isEmbeddedConfluence && !isSnippetCreateEnabled) {
			return;
		}
		snippetsActions.fetchSnippets().catch(() => {
			fireFailedAnalytic(ANALYTICS_OPERATIONAL_SUBJECT.SNIPPET_API_FETCH, {});
		});
	}, [snippetsActions, fireFailedAnalytic, isEmbeddedConfluence, isSnippetCreateEnabled]);

	// Render children immediately - the fetch happens in the background
	return <>{children}</>;
};
